'use client'

import { Image } from '@/components'
import { URL_ICON } from '@/constants/ui'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import { Box, IconButton, Stack, styled, Typography } from '@mui/material'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useState } from 'react'
import 'swiper/css/pagination'
import { Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { getReels } from '../services'
import { formatNumber } from '@/utils/helpers'
import { PopupWatcherReels } from './PopupWatchReel'
import { TitleIcon } from './title-icon'
const StyledSwiper = styled(Swiper)(({ theme }) => ({
  '&.swiper': {
    '& .swiper-wrapper': {
      position: 'relative',
      margin: '0 -8px',
    },
  },
}))

const StyledBtnArrow = styled(IconButton)(({ theme }) => ({
  display: 'flex',
  width: 40,
  aspectRatio: '1/1',
  bgcolor: 'transparent',
  border: `1px solid ${theme.palette.primary.main}`,
  color: theme.palette.primary.main,
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '999px',
  '&:hover:not(.swiper-button-disabled)': { backgroundColor: theme.palette.primary.main, color: 'white' },
  '&.swiper-button-disabled': {
    opacity: 0.6,
    cursor: 'default',
    pointerEvent: 'none',
  },
}))

const StyledSwiperSlide = styled(SwiperSlide)(({ theme }) => ({
  width: 'calc(100% / 4)!important',
  padding: '0 8px',
  [theme.breakpoints.down(1400)]: {
    width: '20%!important',
  },
  [theme.breakpoints.down(600)]: {
    width: 'calc(100%/2.5)!important',
  },
}))

export const Reel = () => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['reels'],

    queryFn: ({ pageParam = 1 }) => {
      return getReels({
        pageSize: 12,
        currentPage: pageParam,
      })
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      if (pages.length < (lastPage?.total || 0) / (lastPage?.page_size || 0)) {
        return pages.length + 1
      }
      return undefined
    },
  })
  const reels = data?.pages.flatMap((page) => page?.items) ?? []
  // handle popup
  const [open, setOpen] = useState(false)
  const [currentReel, setCurrentReel] = useState<any>(null)
  if (reels?.length === 0 && !isLoading) {
    return null
  }
  return (
    <Stack spacing={2}>
      <Stack direction={'row'} spacing={2} justifyContent={'space-between'}>
        <TitleIcon
          typographyProps={{
            variant: 'h2',
            fontSize: { xs: '20px', sm: '26px', md: '28px', lg: '32px' },
            fontWeight: 800,
            lineHeight: 1.5,
          }}
          title="HYDRA REEL"
          underline={false}
          icon={<IconReelTitle />}
        />
        <Stack
          sx={{ display: { xs: 'none', sm: 'flex' } }}
          direction={'row'}
          alignItems={'center'}
          justifyContent={'center'}
          gap={1}
        >
          <StyledBtnArrow className="swiper-button-prev-reel">
            <ArrowBackIosNewIcon sx={{ color: 'inherit', fontSize: '14px' }} />
          </StyledBtnArrow>
          <StyledBtnArrow className="swiper-button-next-reel">
            <ArrowBackIosNewIcon sx={{ transform: 'rotate(180deg)', color: 'inherit', fontSize: '14px' }} />
          </StyledBtnArrow>
        </Stack>
      </Stack>
      <Box sx={{ overflow: 'hidden', position: 'relative' }}>
        <StyledSwiper
          modules={[Pagination, Navigation]}
          navigation={{
            enabled: true,
            nextEl: '.swiper-button-next-reel',
            prevEl: '.swiper-button-prev-reel',
          }}
          slidesPerView={'auto'}
          observer={true}
          observeParents={true}
          // pagination={{
          //   clickable: true,
          //   type: 'bullets',
          //   el: '.swiper-pagination-custom',
          //   dynamicBullets: true,
          //   dynamicMainBullets: 5,
          // }}
          onSlideChange={(swiper) => {
            const currentIndex = swiper.activeIndex
            const totalSlides = swiper.slides.length
            if (currentIndex >= totalSlides - 6 && hasNextPage && !isFetchingNextPage) {
              fetchNextPage()
            }
          }}
          onClick={(swiper) => {
            setCurrentReel(swiper.clickedIndex)
            setOpen(true)
          }}
        >
          {isLoading && (
            <>
              {[...Array(4)].map((_, index) => (
                <StyledSwiperSlide key={`skeleton-${index}`}>
                  <Box
                    className="animate-pulse"
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: '8px',
                      bg: 'secondary.main',
                      aspectRatio: '9/16',
                      display: 'flex',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 100%)',
                      },
                    }}
                  >
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', padding: '0.5em' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.4em' }}>
                        <Box
                          sx={{
                            height: '1em',
                            width: '1em',
                            flexShrink: 0,
                            borderRadius: '50%',
                            backgroundColor: 'gray.500',
                            dark: { backgroundColor: 'muidark.900' },
                          }}
                        />
                        <Box
                          sx={{
                            height: '0.9em',
                            width: '3em',
                            borderRadius: '50%',
                            backgroundColor: 'gray.500',
                            dark: { backgroundColor: 'muidark.900' },
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </StyledSwiperSlide>
              ))}
            </>
          )}
          {reels?.map((reel, index) => (
            <StyledSwiperSlide key={index}>
              <Box
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  aspectRatio: '9/16',
                  overflow: 'hidden',
                }}
              >
                <Image
                  src={reel?.thumbnail || ''}
                  alt={reel?.title || 'Video ngắn sản phẩm'}
                  className="img-cover my-auto block duration-300 hover:scale-105"
                />
                <Box
                  component={'span'}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: '100%',
                    width: '100%',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 100%)',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    padding: '0.5em',
                    color: 'white',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4em',
                      fontSize: '0.9em',
                      fontWeight: 500,
                    }}
                  >
                    <img src={URL_ICON + 'ic-play.svg'} alt="Phát video" style={{ width: '1em', flexShrink: 0 }} />
                    <Box component={'span'}>
                      {/* {formatNumber(reel?.total_view || 0)} */}
                      {(() => {
                        // nếu total_view = 0 hoặc là boolean -> fake theo index, cố định, không tăng dần
                        const isInvalid = !reel?.total_view

                        // tạo số giả ổn định dựa trên index (deterministic, không phải dãy tăng)
                        const getStableFakeNumber = (i: number): number => {
                          // hash nhẹ bằng sin, đảm bảo phân bố và không tuyến tính
                          const seed = Math.abs(Math.sin(i * 13.37) + Math.cos(i * 7.77))
                          // map vào khoảng 500 → 25_000 cho nhìn thực tế
                          return Math.floor(500 + seed * 2450)
                        }

                        const views = isInvalid ? getStableFakeNumber(index) : (reel?.total_view as number)
                        return <Box component={'span'}>{formatNumber(views)}</Box>
                      })()}
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  marginTop: 1,
                  lineClamp: 2,
                  overflow: 'hidden',
                  WebkitBoxOrient: 'vertical',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                }}
              >
                {reel?.title}
              </Typography>
            </StyledSwiperSlide>
          ))}
        </StyledSwiper>
        {/* <StyledPagination className="swiper-pagination-custom"></StyledPagination> */}
        <PopupWatcherReels
          reels={reels}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          open={open}
          setOpen={setOpen}
          currentReel={currentReel}
        />
      </Box>
    </Stack>
  )
}

const IconReelTitle = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path
        d="M18.0006 3.99994H6.00059C4.90107 3.99994 4.00049 4.90053 4.00049 6.00004V18C4.00049 19.0995 4.90107 20.0001 6.00059 20.0001H18.0006C19.1001 20.0001 20.0007 19.0995 20.0007 18V6.00004C20.0007 4.90053 19.1001 3.99994 18.0006 3.99994ZM10.2507 15V9.00004L14.7507 12L10.2507 15Z"
        fill="white"
      />
      <path
        d="M0.999902 6.00006C0.450293 6.00006 0 6.45035 0 6.99996V17.0002C0 17.5498 0.450293 18.0001 0.999902 18.0001H2.4999V6.00006H0.999902Z"
        fill="white"
      />
      <path
        d="M23 6.00006H21.5V18.0001H23C23.5496 18.0001 23.9999 17.5498 23.9999 17.0002V6.99996C23.9999 6.45035 23.5496 6.00006 23 6.00006Z"
        fill="white"
      />
    </svg>
  )
}
